const en = {
  common: {
    administration: 'Administration', allStatuses: 'All statuses', active: 'Active',
    inactive: 'Inactive', status: 'Status', actions: 'Actions', edit: 'Edit',
    activate: 'Activate', deactivate: 'Deactivate', close: 'Close', cancel: 'Cancel',
    saving: 'Saving…', remove: 'Remove', previous: 'Previous', next: 'Next',
  },
  locales: { en: 'English', ja: 'Japanese', zhTW: 'Traditional Chinese', ko: 'Korean' },
  scopeTypes: {
    OWN: 'Own', ASSIGNED: 'Assigned', TEAM: 'Team', DEPARTMENT: 'Department',
    BRANCH: 'Branch', COUNTRY: 'Country', SELECTED_LOCATION: 'Selected location',
    ORGANIZATION: 'Organization',
  },
  scopeTargets: { country: 'Country', branch: 'Branch', department: 'Department', team: 'Team' },
  users: {
    navigation: 'Users', title: 'Users', description: 'Manage accounts, organizational placement, roles, and data scopes.',
    create: 'Create user', edit: 'Edit user', search: 'Search users', loading: 'Loading users…',
    loadingDetail: 'Loading user…', loadDetailError: 'Unable to load user.',
    emptyTitle: 'No users found', emptyDescription: 'Change the filters or create an account.',
    allRoles: 'All roles', allCountries: 'All countries', allBranches: 'All branches',
    allDepartments: 'All departments', allTeams: 'All teams', noPlacement: 'No primary placement',
    editorLabel: 'User editor', separationHelp: 'Organizational placement is separate from authorization scope.',
    passwordHelp: '8–16 characters with uppercase, lowercase, number, and special character.',
    placement: 'Organizational placement', roles: 'Roles', dataScopes: 'Data scopes',
    noCountry: 'No country', noBranch: 'No branch', noDepartment: 'No department', noTeam: 'No team',
    selectTarget: 'Select target', noTarget: 'No target required', addScope: 'Add scope',
    saveProfile: 'Save profile', saveRoles: 'Save roles', saveScopes: 'Save data scopes',
    userRequired: 'Select a user before changing access.',
    columns: { user: 'User', roles: 'Roles', placement: 'Placement' },
    fields: { firstName: 'First name', lastName: 'Last name', email: 'Email', language: 'Language', initialPassword: 'Initial password' },
  },
  roles: {
    navigation: 'Roles', title: 'Roles', description: 'Permissions from multiple assigned roles combine additively.',
    create: 'Create role', edit: 'Edit role', search: 'Search roles', loading: 'Loading roles…',
    empty: 'No roles found.', system: 'System', editorLabel: 'Role editor', permissions: 'Permissions',
    permissionHelp: 'Permission codes remain server-authoritative.',
    loadPermissionsError: 'Unable to load permissions.', roleRequired: 'Select a role before changing permissions.',
    saveProfile: 'Save role details', savePermissions: 'Save permissions',
    columns: { role: 'Role', code: 'Code', permissions: 'Permissions', users: 'Users' },
    fields: { name: 'Name', code: 'Code', description: 'Description' },
  },
  permissions: {
    navigation: 'Permissions', title: 'Permissions', description: 'The permission catalog is code-defined and read-only.',
    search: 'Search permissions', loading: 'Loading permissions…', empty: 'No permissions found.',
  },
  denied: {
    eyebrow: 'Access restricted', title: 'Permission required',
    description: 'Your account does not have permission to open this page.', returnHome: 'Return home',
  },
} as const;

const ja = {
  common: {
    administration: '管理', allStatuses: 'すべてのステータス', active: '有効', inactive: '無効',
    status: 'ステータス', actions: '操作', edit: '編集', activate: '有効化', deactivate: '無効化',
    close: '閉じる', cancel: 'キャンセル', saving: '保存中…', remove: '削除', previous: '前へ', next: '次へ',
  },
  locales: { en: '英語', ja: '日本語', zhTW: '繁体字中国語', ko: '韓国語' },
  scopeTypes: {
    OWN: '本人所有', ASSIGNED: '割り当て済み', TEAM: 'チーム', DEPARTMENT: '部門', BRANCH: '支店',
    COUNTRY: '国', SELECTED_LOCATION: '選択した拠点', ORGANIZATION: '組織全体',
  },
  scopeTargets: { country: '国', branch: '支店', department: '部門', team: 'チーム' },
  users: {
    navigation: 'ユーザー', title: 'ユーザー', description: 'アカウント、所属、ロール、データ範囲を管理します。',
    create: 'ユーザーを作成', edit: 'ユーザーを編集', search: 'ユーザーを検索', loading: 'ユーザーを読み込み中…',
    loadingDetail: 'ユーザーを読み込み中…', loadDetailError: 'ユーザーを読み込めません。',
    emptyTitle: 'ユーザーが見つかりません', emptyDescription: 'フィルターを変更するか、アカウントを作成してください。',
    allRoles: 'すべてのロール', allCountries: 'すべての国', allBranches: 'すべての支店',
    allDepartments: 'すべての部門', allTeams: 'すべてのチーム', noPlacement: '主所属なし',
    editorLabel: 'ユーザー編集', separationHelp: '組織上の所属と認可範囲は別に管理されます。',
    passwordHelp: '8～16文字で、大文字・小文字・数字・記号を含めてください。',
    placement: '組織上の所属', roles: 'ロール', dataScopes: 'データ範囲',
    noCountry: '国なし', noBranch: '支店なし', noDepartment: '部門なし', noTeam: 'チームなし',
    selectTarget: '対象を選択', noTarget: '対象は不要です', addScope: '範囲を追加',
    saveProfile: 'プロフィールを保存', saveRoles: 'ロールを保存', saveScopes: 'データ範囲を保存',
    userRequired: 'アクセスを変更する前にユーザーを選択してください。',
    columns: { user: 'ユーザー', roles: 'ロール', placement: '所属' },
    fields: { firstName: '名', lastName: '姓', email: 'メール', language: '言語', initialPassword: '初期パスワード' },
  },
  roles: {
    navigation: 'ロール', title: 'ロール', description: '複数ロールの権限は加算されます。',
    create: 'ロールを作成', edit: 'ロールを編集', search: 'ロールを検索', loading: 'ロールを読み込み中…',
    empty: 'ロールが見つかりません。', system: 'システム', editorLabel: 'ロール編集', permissions: '権限',
    permissionHelp: '権限コードはサーバーが管理します。', loadPermissionsError: '権限を読み込めません。',
    roleRequired: '権限を変更する前にロールを選択してください。', saveProfile: 'ロール詳細を保存', savePermissions: '権限を保存',
    columns: { role: 'ロール', code: 'コード', permissions: '権限', users: 'ユーザー' },
    fields: { name: '名前', code: 'コード', description: '説明' },
  },
  permissions: {
    navigation: '権限', title: '権限', description: '権限カタログはコードで定義され、読み取り専用です。',
    search: '権限を検索', loading: '権限を読み込み中…', empty: '権限が見つかりません。',
  },
  denied: { eyebrow: 'アクセス制限', title: '権限が必要です', description: 'このページを開く権限がありません。', returnHome: 'ホームへ戻る' },
} as const;

const zhTW = {
  common: {
    administration: '管理', allStatuses: '所有狀態', active: '啟用', inactive: '停用', status: '狀態', actions: '操作',
    edit: '編輯', activate: '啟用', deactivate: '停用', close: '關閉', cancel: '取消', saving: '儲存中…',
    remove: '移除', previous: '上一頁', next: '下一頁',
  },
  locales: { en: '英文', ja: '日文', zhTW: '繁體中文', ko: '韓文' },
  scopeTypes: {
    OWN: '本人擁有', ASSIGNED: '已指派', TEAM: '團隊', DEPARTMENT: '部門', BRANCH: '分公司',
    COUNTRY: '國家', SELECTED_LOCATION: '指定地點', ORGANIZATION: '全組織',
  },
  scopeTargets: { country: '國家', branch: '分公司', department: '部門', team: '團隊' },
  users: {
    navigation: '使用者', title: '使用者', description: '管理帳號、組織歸屬、角色與資料範圍。',
    create: '建立使用者', edit: '編輯使用者', search: '搜尋使用者', loading: '正在載入使用者…',
    loadingDetail: '正在載入使用者…', loadDetailError: '無法載入使用者。',
    emptyTitle: '找不到使用者', emptyDescription: '請調整篩選條件或建立帳號。',
    allRoles: '所有角色', allCountries: '所有國家', allBranches: '所有分公司', allDepartments: '所有部門',
    allTeams: '所有團隊', noPlacement: '沒有主要歸屬', editorLabel: '使用者編輯器',
    separationHelp: '組織歸屬與授權資料範圍分開管理。', passwordHelp: '8–16 個字元，須包含大小寫字母、數字與特殊字元。',
    placement: '組織歸屬', roles: '角色', dataScopes: '資料範圍', noCountry: '無國家', noBranch: '無分公司',
    noDepartment: '無部門', noTeam: '無團隊', selectTarget: '選擇目標', noTarget: '不需要目標', addScope: '新增範圍',
    saveProfile: '儲存個人資料', saveRoles: '儲存角色', saveScopes: '儲存資料範圍',
    userRequired: '變更存取權前請先選擇使用者。',
    columns: { user: '使用者', roles: '角色', placement: '歸屬' },
    fields: { firstName: '名字', lastName: '姓氏', email: '電子郵件', language: '語言', initialPassword: '初始密碼' },
  },
  roles: {
    navigation: '角色', title: '角色', description: '使用者擁有多個角色時，權限會相加。',
    create: '建立角色', edit: '編輯角色', search: '搜尋角色', loading: '正在載入角色…', empty: '找不到角色。',
    system: '系統', editorLabel: '角色編輯器', permissions: '權限', permissionHelp: '權限代碼由伺服器管理。',
    loadPermissionsError: '無法載入權限。', roleRequired: '變更權限前請先選擇角色。',
    saveProfile: '儲存角色資料', savePermissions: '儲存權限',
    columns: { role: '角色', code: '代碼', permissions: '權限', users: '使用者' },
    fields: { name: '名稱', code: '代碼', description: '說明' },
  },
  permissions: {
    navigation: '權限', title: '權限', description: '權限目錄由程式碼定義且為唯讀。',
    search: '搜尋權限', loading: '正在載入權限…', empty: '找不到權限。',
  },
  denied: { eyebrow: '存取受限', title: '需要權限', description: '您的帳號沒有開啟此頁面的權限。', returnHome: '返回首頁' },
} as const;

const ko = {
  common: {
    administration: '관리', allStatuses: '모든 상태', active: '활성', inactive: '비활성', status: '상태', actions: '작업',
    edit: '편집', activate: '활성화', deactivate: '비활성화', close: '닫기', cancel: '취소', saving: '저장 중…',
    remove: '제거', previous: '이전', next: '다음',
  },
  locales: { en: '영어', ja: '일본어', zhTW: '중국어 번체', ko: '한국어' },
  scopeTypes: {
    OWN: '본인 소유', ASSIGNED: '배정됨', TEAM: '팀', DEPARTMENT: '부서', BRANCH: '지점', COUNTRY: '국가',
    SELECTED_LOCATION: '선택 위치', ORGANIZATION: '전체 조직',
  },
  scopeTargets: { country: '국가', branch: '지점', department: '부서', team: '팀' },
  users: {
    navigation: '사용자', title: '사용자', description: '계정, 조직 배치, 역할 및 데이터 범위를 관리합니다.',
    create: '사용자 만들기', edit: '사용자 편집', search: '사용자 검색', loading: '사용자 불러오는 중…',
    loadingDetail: '사용자 불러오는 중…', loadDetailError: '사용자를 불러올 수 없습니다.',
    emptyTitle: '사용자가 없습니다', emptyDescription: '필터를 변경하거나 계정을 만드세요.',
    allRoles: '모든 역할', allCountries: '모든 국가', allBranches: '모든 지점', allDepartments: '모든 부서',
    allTeams: '모든 팀', noPlacement: '기본 배치 없음', editorLabel: '사용자 편집기',
    separationHelp: '조직 배치와 권한 데이터 범위는 별도로 관리됩니다.',
    passwordHelp: '8~16자이며 대문자, 소문자, 숫자, 특수 문자를 포함해야 합니다.',
    placement: '조직 배치', roles: '역할', dataScopes: '데이터 범위', noCountry: '국가 없음', noBranch: '지점 없음',
    noDepartment: '부서 없음', noTeam: '팀 없음', selectTarget: '대상 선택', noTarget: '대상 필요 없음', addScope: '범위 추가',
    saveProfile: '프로필 저장', saveRoles: '역할 저장', saveScopes: '데이터 범위 저장',
    userRequired: '접근 권한을 변경하기 전에 사용자를 선택하세요.',
    columns: { user: '사용자', roles: '역할', placement: '배치' },
    fields: { firstName: '이름', lastName: '성', email: '이메일', language: '언어', initialPassword: '초기 비밀번호' },
  },
  roles: {
    navigation: '역할', title: '역할', description: '여러 역할의 권한은 합산됩니다.',
    create: '역할 만들기', edit: '역할 편집', search: '역할 검색', loading: '역할 불러오는 중…', empty: '역할이 없습니다.',
    system: '시스템', editorLabel: '역할 편집기', permissions: '권한', permissionHelp: '권한 코드는 서버에서 관리합니다.',
    loadPermissionsError: '권한을 불러올 수 없습니다.', roleRequired: '권한을 변경하기 전에 역할을 선택하세요.',
    saveProfile: '역할 정보 저장', savePermissions: '권한 저장',
    columns: { role: '역할', code: '코드', permissions: '권한', users: '사용자' },
    fields: { name: '이름', code: '코드', description: '설명' },
  },
  permissions: {
    navigation: '권한', title: '권한', description: '권한 목록은 코드로 정의되며 읽기 전용입니다.',
    search: '권한 검색', loading: '권한 불러오는 중…', empty: '권한이 없습니다.',
  },
  denied: { eyebrow: '접근 제한', title: '권한 필요', description: '이 페이지를 열 권한이 없습니다.', returnHome: '홈으로 돌아가기' },
} as const;

export const accessMessages = { en, ja, 'zh-TW': zhTW, ko } as const;
